/**
 * RoleSurface — the shared role-oriented surface shell (FULL-0155 / PROD-UX-069, MASTER_FLOW §14C.5).
 *
 * It replaces the nearly identical page wrappers that had drifted across the Manager Console, the
 * Workforce Console, the Attention Inbox and the Admin permissions screen: one max-width, one
 * padding/gap scale, one operational type scale, one density switch and one set of logical
 * (RTL-safe) spacing rules.
 *
 * THREE DELIBERATE LIMITS:
 *   1. Presentation only. The props describe a workspace; they never grant a role, a permission or
 *      an action. `ROLE_SURFACE_AUTHORITY` records this and the tests assert it.
 *   2. Hiding, never authorizing. An optional `permission` prop can only REMOVE children from the
 *      tree. It fails closed while permissions are loading or after a failed load, and an unknown
 *      `role` renders nothing. The server and the existing `AgentPermissionsContext` stay the
 *      authority.
 *   3. Direction is inherited. `dir` is passed through when a surface already pins one; the shell
 *      does not hard-code a reading direction, and every spacing rule uses logical properties.
 */
import { useAgentPermissions } from "../../context/AgentPermissionsContext";
import {
  DEFAULT_DENSITY,
  DENSITY_KEYS,
  ROLE_SURFACE_AUTHORITY,
  roleWorkspaceMeta,
} from "./designTokens";

export default function RoleSurface({
  role,
  title = null,
  description = null,
  actions = null,
  meta = null,
  children = null,
  permission = null,
  density = DEFAULT_DENSITY,
  width = "standard",
  padding = "section",
  dir,
  className = "",
  ariaLabel = null,
  testId,
}) {
  const workspace = roleWorkspaceMeta(role);
  const permissions = useAgentPermissions() || {};
  const { can, loading = false, failed = false } = permissions;

  // An unrecognised workspace is not guessed into a nearby role.
  if (!workspace) return null;

  // Fail closed: a permission that is still loading, failed to load, or is absent hides the
  // surface. This is a visibility decision only; it never grants anything.
  if (permission) {
    if (loading || failed) return null;
    if (typeof can !== "function" || !can(permission)) return null;
  }

  const densityKey = DENSITY_KEYS.includes(density) ? density : DEFAULT_DENSITY;
  const label = ariaLabel || (typeof title === "string" && title.trim() ? title : workspace.label);

  const renderedDescription =
    typeof description === "string" ? (
      <p className="prodigy-text-body prodigy-role-surface__description">{description}</p>
    ) : (
      description
    );
  const renderedMeta =
    typeof meta === "string" ? (
      <p className="prodigy-role-surface__meta">{meta}</p>
    ) : (
      meta
    );
  const hasHeader = Boolean(title || renderedDescription || actions || renderedMeta);

  return (
    <section
      dir={dir}
      aria-label={label}
      className={`prodigy-role-surface${className ? ` ${className}` : ""}`}
      data-role-surface={workspace.key}
      data-role-flow-ref={workspace.flowRef}
      data-density={densityKey}
      data-width={width}
      data-padding={padding}
      data-permission={permission || undefined}
      data-presentation-only={ROLE_SURFACE_AUTHORITY.presentationOnly ? "true" : undefined}
      data-testid={testId}
    >
      {hasHeader && (
        <header className="prodigy-role-surface__header">
          <div className="prodigy-role-surface__heading-row">
            {title && <h1 className="prodigy-text-title prodigy-role-surface__title">{title}</h1>}
            {actions && <div className="prodigy-role-surface__actions">{actions}</div>}
          </div>
          {renderedDescription}
          {renderedMeta}
        </header>
      )}
      {children}
    </section>
  );
}
